import { Drawer } from '@mantine/core';
import clsx from 'clsx';
import { useCallback, useRef } from 'react';
import { darkModeRoot } from 'src/app/App.css';
import { useSiteTheme } from 'src/contexts/SiteTheme/SiteThemeContext';
import { vars } from 'src/core/themes';
import { globalMobileWrapper } from 'src/core/themes/themeContract.css';
import { Stack } from 'src/core/ui';

import { StrategyCalculatorContent } from './StrategyCalculatorPanel';
import { DRAG_CLOSE_THRESHOLD } from './StrategyCalculatorPanel.constants';
import * as styles from './StrategyCalculatorPanel.css';

export const StrategyCalculatorDrawer = ({
  isOpen,
  onClose,
  eventDateStr,
  onRecommendationChange,
}: {
  isOpen: boolean;
  onClose: () => void;
  eventDateStr?: string | null;
  onRecommendationChange?: (strategyId: string) => void;
}) => {
  const { isDarkMode } = useSiteTheme();
  const contentRef = useRef<HTMLDivElement>(null);
  const dragStartY = useRef<number | null>(null);
  const dragDelta = useRef(0);

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    dragStartY.current = e.touches[0].clientY;
    dragDelta.current = 0;
  }, []);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    if (dragStartY.current === null) return;
    const delta = e.touches[0].clientY - dragStartY.current;
    dragDelta.current = delta;

    if (contentRef.current && delta > 0) {
      contentRef.current.style.transform = `translateY(${delta}px)`;
      contentRef.current.style.transition = 'none';
    }
  }, []);

  const handleTouchEnd = useCallback(() => {
    if (dragDelta.current > DRAG_CLOSE_THRESHOLD) {
      if (contentRef.current) {
        contentRef.current.style.transition = 'transform 0.3s ease-in';
        contentRef.current.style.transform = 'translateY(100%)';
        contentRef.current.addEventListener('transitionend', () => onClose(), {
          once: true,
        });
      } else {
        onClose();
      }
    } else if (contentRef.current) {
      contentRef.current.style.transition = 'transform 0.2s ease-out';
      contentRef.current.style.transform = 'translateY(0)';
    }

    dragStartY.current = null;
    dragDelta.current = 0;
  }, [onClose]);

  return (
    <Drawer.Root
      opened={isOpen}
      onClose={onClose}
      size="90%"
      position="bottom"
      zIndex={1060}
      className={clsx(globalMobileWrapper, {
        [darkModeRoot]: isDarkMode,
      })}
      closeOnClickOutside
    >
      <Drawer.Overlay />
      <Drawer.Content
        ref={contentRef}
        bg={vars.color.backgroundSecondary}
        className={styles.drawerBorderRadius}
        radius="md"
      >
        <Drawer.Header
          bg={vars.color.backgroundSecondary}
          className={styles.drawerHeader}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          <Stack
            justifyContent="center"
            alignItems="center"
            width="full"
            direction="column"
          >
            <div className={styles.drawerHandleBar} />
          </Stack>
        </Drawer.Header>
        <Drawer.Body
          bg={vars.color.backgroundSecondary}
          className={styles.drawerBody}
        >
          <StrategyCalculatorContent
            onClose={onClose}
            showCloseButton={false}
            panelClassName={styles.drawerPanel}
            isMobile
            eventDateStr={eventDateStr}
            onRecommendationChange={onRecommendationChange}
          />
        </Drawer.Body>
      </Drawer.Content>
    </Drawer.Root>
  );
};
